import { useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  editFormDataAF,
  getEffectiveMaxSegmentDurationMinutes,
  getRideBoardingSegmentDurationMinutesCeil,
  isRideBoardingSegmentDurationIndicatorVisible,
  RideCrudModeKeyTuple,
  systemSettingsDtoAtom,
  type IRideDto,
} from "@ryde/shared";

export interface IRideBoardingSegmentDurationLabelState {
  rideDto: Partial<IRideDto> | undefined;
  effectiveMaxSegmentDurationInMinutes: number | undefined;
  boardingSegmentDurationMinutesCeil: number | undefined;
  isBoardingSegmentDurationExceeded: boolean;
  isVisible: boolean;
}

export const useRideBoardingSegmentDurationLabel =
  (): IRideBoardingSegmentDurationLabelState => {
    const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
      editFormDataAF(RideCrudModeKeyTuple),
    );
    const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);

    const isVisible = useMemo(() => {
      return isRideBoardingSegmentDurationIndicatorVisible({
        metadata: rideDto?.metadata,
        direction: rideDto?.direction,
      });
    }, [rideDto?.direction, rideDto?.metadata]);

    const effectiveMaxSegmentDurationInMinutes = useMemo(() => {
      return getEffectiveMaxSegmentDurationMinutes({
        priceRateMaxBoardingSegmentDurationInMinutes:
          rideDto?.priceRateMaxBoardingSegmentDurationInMinutes,
        systemParams: systemSettingsDto?.metadata,
        direction: rideDto?.direction,
      });
    }, [
      rideDto?.direction,
      rideDto?.priceRateMaxBoardingSegmentDurationInMinutes,
      systemSettingsDto?.metadata,
    ]);

    const boardingSegmentDurationMinutesCeil = useMemo(() => {
      if (!isVisible) {
        return undefined;
      }
      const minutes = getRideBoardingSegmentDurationMinutesCeil({
        metadata: rideDto?.metadata,
        direction: rideDto?.direction,
      });
      return minutes > 0 ? minutes : undefined;
    }, [isVisible, rideDto?.direction, rideDto?.metadata]);

    const isBoardingSegmentDurationExceeded = useMemo(() => {
      return (
        !!boardingSegmentDurationMinutesCeil &&
        !!effectiveMaxSegmentDurationInMinutes &&
        effectiveMaxSegmentDurationInMinutes > 0 &&
        boardingSegmentDurationMinutesCeil >
          effectiveMaxSegmentDurationInMinutes
      );
    }, [
      boardingSegmentDurationMinutesCeil,
      effectiveMaxSegmentDurationInMinutes,
    ]);

    return {
      rideDto,
      effectiveMaxSegmentDurationInMinutes,
      boardingSegmentDurationMinutesCeil,
      isBoardingSegmentDurationExceeded,
      isVisible,
    };
  };
